<template>
    <div class="c-hd" :style="wrapStyle">
        <div :style="innerStyle">
            <div :style="topLineStyle">
                <a href="#" @click.prevent="onPrevPage" :style="prevLinkStyle">
                    ← Back to summary
                </a>
            </div>

            <div :style="headerStyle">
                <div :style="eyebrowStyle">{{ agencyName }}</div>
                <h1 :style="h1Style">Historical data</h1>
                <p :style="leadStyle">
                    Every recorded sale line across all of your periods. Read-only
                    — useful for spotting trends and cross-checking commission.
                </p>
            </div>

            <CCard :extra-style="cardStyle">
                <div :style="tableWrapStyle">
                    <table :style="tableStyle">
                        <thead>
                            <tr :style="trHeadStyle">
                                <th :style="thStyle">Period</th>
                                <th :style="thStyle">Brand</th>
                                <th :style="thStyle">Service</th>
                                <th :style="thStyleRight">Amount</th>
                                <th :style="thStyleRight">Hard cost</th>
                                <th :style="thStyleRight">Commission %</th>
                                <th :style="thStyleRight">Commission $</th>
                                <th :style="thStyleRight">Collected</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-if="data.length > 0">
                                <tr
                                    v-for="(sli, index) in data"
                                    :key="`historical_item_${index}`"
                                    :style="trBodyStyle(index)"
                                >
                                    <td :style="tdStyle">{{ getPeriodDate(sli.period) }}</td>
                                    <td :style="tdStyleStrong">{{ sli.brand }}</td>
                                    <td :style="tdStyle">{{ sli.service }}</td>
                                    <td :style="tdStyleNum">${{ sli.amount }}</td>
                                    <td :style="tdStyleNum">${{ sli.hard_cost }}</td>
                                    <td :style="tdStyleNum">{{ sli.commission_percent }}%</td>
                                    <td :style="tdStyleNumAccent">${{ sli.commission_value }}</td>
                                    <td :style="tdStyleNum">${{ sli.collected }}</td>
                                </tr>
                            </template>
                            <template v-else>
                                <tr>
                                    <td :style="tdEmptyStyle" colspan="8">No data</td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </CCard>

            <div :style="navStyle">
                <CBtn ghost @click="onPrevPage">← Back to summary</CBtn>
                <span :style="navMetaStyle">
                    {{ data.length }} {{ data.length === 1 ? 'record' : 'records' }}
                </span>
            </div>

            <div v-if="isLoading" :style="loaderStyle">
                <clip-loader :loading="isLoading" color="#ec4657" size="45px" />
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import ClipLoader from "vue-spinner/src/ClipLoader";

import { SECTIONS } from "../consts";

import CCard from "../c-design/CCard.vue";
import CBtn from "../c-design/CBtn.vue";
import viewportMixin from "../c-design/useViewport";
import {
    C_BG,
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    TAG_RED_DEEP,
    FONT_DISPLAY,
    FONT_BODY,
} from "../c-design/tokens";

export default {
    name: "Wizard_Hystorical_Data",
    mixins: [viewportMixin],
    props: ["onChangeSection"],
    components: {
        ClipLoader,
        CCard,
        CBtn,
    },
    data() {
        return {
            data: [],
            isLoading: false,
        };
    },
    mounted() {
        this.getData();
    },
    computed: {
        agencyName() {
            return this.$store.getters.getAgencyName;
        },
        // ===== c-design style maps =====
        wrapStyle() {
            return {
                background: C_BG,
                minHeight: '70vh',
                padding: this.isMobile ? '14px 12px 28px' : '24px 16px 40px',
            };
        },
        innerStyle() {
            return { maxWidth: '1200px', margin: '0 auto' };
        },
        topLineStyle() {
            return {
                marginBottom: '8px',
                padding: this.isMobile ? '0 4px' : '0 8px',
            };
        },
        prevLinkStyle() {
            return {
                fontSize: '13px',
                color: C_MUTED,
                textDecoration: 'none',
                borderBottom: `1px solid ${C_HAIR}`,
            };
        },
        headerStyle() {
            return { marginBottom: '20px', padding: '0 8px' };
        },
        eyebrowStyle() {
            return {
                fontSize: '12px', color: C_MUTED, letterSpacing: '0.6px',
                textTransform: 'uppercase', fontWeight: 600,
            };
        },
        h1Style() {
            return {
                margin: '6px 0 8px', fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '26px' : '34px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-0.7px' : '-1px',
                lineHeight: 1.1, color: C_INK,
            };
        },
        leadStyle() {
            return {
                margin: 0, fontSize: '14px', color: C_MUTED,
                lineHeight: 1.55, maxWidth: '600px',
            };
        },
        cardStyle() {
            return { marginBottom: '18px', padding: '0' };
        },
        tableWrapStyle() {
            return {
                overflowX: 'auto',
                borderRadius: '24px',
            };
        },
        tableStyle() {
            return {
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px',
            };
        },
        trHeadStyle() {
            return {
                background: C_INK,
                color: '#fff',
                position: 'sticky',
                top: 0,
            };
        },
        thStyle() {
            return {
                padding: '14px 16px',
                textAlign: 'left',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
            };
        },
        thStyleRight() {
            return { ...this.thStyle, textAlign: 'right' };
        },
        tdStyle() {
            return {
                padding: '12px 16px',
                fontSize: '13px',
                color: C_INK2,
                fontFamily: FONT_BODY,
                whiteSpace: 'nowrap',
            };
        },
        tdStyleStrong() {
            return {
                padding: '12px 16px',
                fontSize: '13px',
                color: C_INK,
                fontFamily: FONT_DISPLAY,
                fontWeight: 600,
                whiteSpace: 'nowrap',
            };
        },
        tdStyleNum() {
            return {
                padding: '12px 16px',
                fontSize: '13px',
                color: C_INK,
                fontFamily: FONT_DISPLAY,
                fontWeight: 500,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
            };
        },
        tdStyleNumAccent() {
            return { ...this.tdStyleNum, color: TAG_RED_DEEP, fontWeight: 700 };
        },
        tdEmptyStyle() {
            return {
                padding: '40px',
                textAlign: 'center',
                color: C_MUTED,
                fontSize: '14px',
            };
        },
        navStyle() {
            return {
                marginTop: '8px', padding: '16px 24px',
                background: '#fff', borderRadius: '16px',
                border: `1px solid ${C_HAIR}`,
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '16px',
            };
        },
        navMetaStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                fontFamily: FONT_DISPLAY,
                fontWeight: 500,
            };
        },
        loaderStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px',
            };
        },
    },
    methods: {
        trBodyStyle(i) {
            return {
                background: i % 2 === 0 ? '#fff' : C_CREAM,
                borderTop: `1px solid ${C_HAIR}`,
            };
        },
        getPeriodDate(date = null) {
            if (date) {
                return moment(date).format("MM/YYYY");
            }
            return "";
        },
        onPrevPage(e) {
            if (e && typeof e.preventDefault === 'function') {
                e.preventDefault();
            }

            this.onChangeSection(
                SECTIONS.FINAL_DASHBOARD,
                SECTIONS.HYSTORICAL_DATA
            );
        },
        async getData() {
            try {
                this.isLoading = true;

                await axios
                    .get("/api/wizard/getHistoricalData")
                    .then((res) => {
                        const { data } = res;

                        this.data = data;
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            } catch (err) {
                console.error(err);
            }
        },
    },
};
</script>
